import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useCurrentUser from '../hooks/useCurrentUser.js';
import Logo from '../components/images/Logo.jsx';
import { ScrollPanel } from 'primereact/scrollpanel';

const Homepage = () => {
    const navigate = useNavigate();
    const { user } = useCurrentUser();

    const scrollRef = useRef(null);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const scrollTimeoutRef = useRef(null);
    const scrollIntervalRef = useRef(null);
    const lastScrollTopRef = useRef(0);
    const isAtBottomRef = useRef(false);
    const isAtTopRef = useRef(false);

    const startAutoScroll = useCallback(() => {
        if (!scrollRef.current || !scrollRef.current.getContent || isUserScrolling || isAtBottomRef.current) return;

        scrollIntervalRef.current = setInterval(() => {
            if (!scrollRef.current || !scrollRef.current.getContent || isUserScrolling || isAtBottomRef.current) {
                clearInterval(scrollIntervalRef.current);
                return;
            }

            const container = scrollRef.current.getContent();

            if (container.scrollTop < container.scrollHeight - container.clientHeight) {
                container.scrollTop += 1;
            } else {
                isAtBottomRef.current = true;
                clearInterval(scrollIntervalRef.current);
                startAutoScroll();
            }
        }, 30);
    }, [isUserScrolling]);

    const handleUserScroll = useCallback(() => {
        const container = scrollRef.current?.getContent();
        if (!container) return;

        const currentScrollTop = container.scrollTop;

        if (currentScrollTop < lastScrollTopRef.current) {
            setIsUserScrolling(true);
            isAtBottomRef.current = false;
        } else {
            setIsUserScrolling(false);
        }

        lastScrollTopRef.current = currentScrollTop;

        if (currentScrollTop === 0) {
            isAtTopRef.current = true;
        } else {
            isAtTopRef.current = false;
        }

        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
            if (!isUserScrolling && !isAtBottomRef.current && !isAtTopRef.current) {
                isAtBottomRef.current = false;
                startAutoScroll();
            }
        }, 2000);
    }, [isUserScrolling, startAutoScroll]);

    const handleEnter = () => {
        if (user) {
            navigate('/dashboard');
        } else {
            navigate('/login');
        }
    };

    useEffect(() => {
        if (user) {
            navigate('/dashboard', { replace: true });
        }
    }, [user, navigate]);

    useEffect(() => {
        const checkScrollPanel = setInterval(() => {
            if (scrollRef.current && scrollRef.current.getContent) {
                clearInterval(checkScrollPanel);
                startAutoScroll();
            }
        }, 500);

        return () => clearInterval(checkScrollPanel);
    }, [startAutoScroll]);

    useEffect(() => {
        const container = scrollRef.current?.getContent();
        if (!container) return;

        container.addEventListener('scroll', handleUserScroll);

        return () => {
            container.removeEventListener('scroll', handleUserScroll);
            clearInterval(scrollIntervalRef.current);
            clearTimeout(scrollTimeoutRef.current);
        };
    }, [handleUserScroll]);

    return (
        <div className='min-h-screen flex flex-col justify-center items-center bg-guest bg-center bg-cover'>
            <div className='p-5 max-w-3xl w-full'>
                <header className='flex items-center justify-center mb-10'>
                    <Logo className='w-60 fill-current text-theme1-white' />
                </header>
                <main className='flex flex-col items-center'>
                    <ScrollPanel
                        ref={scrollRef}
                        style={{ height: '35vh', width: '90%' }}
                        className='text-theme1-white text-[15px] mb-24 font-light text-center'
                    >
                        <h1 className='font-bold text-2xl mb-3'>Welcome to LDR.ai</h1>
                        <p className='text-[15px] leading-7 mb-9'>
                            <span className='font-bold'>LDR.ai is your AI-powered personal assistant</span> designed for
                            busy executives. We unify{' '}
                            <span className='font-bold'>organizations, projects, contacts, and files</span> - all in one
                            place -<span className='font-bold'>to streamline your work and save you time.</span>
                        </p>
                        <h2 className='font-bold text-2xl mb-3'>Calendar & Meetings</h2>
                        <div className='text-[15px] leading-7 mb-10'>
                            <div>
                                We request <span className='font-bold'>Google Calendar access</span> to:
                            </div>

                            <ul className='text-left inline-block'>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    <span className='font-bold'>Create events</span> and meeting invitations
                                </li>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    Generate{' '}
                                    <span className='font-bold'>
                                        helpful descriptions for meeting participants, objectives, obstacles & more
                                    </span>
                                </li>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    <span className='font-bold'>Sync schedules across LDR.ai</span> and your calendar
                                </li>
                            </ul>
                        </div>

                        <h2 className='font-bold text-2xl mb-3'>Data-Driven Insights</h2>
                        <div className='text-[15px] leading-7 mb-10'>
                            <div>
                                Our AI uses <span className='font-bold'>meeting info & company data</span> to:
                            </div>

                            <ul className='text-left inline-block'>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    Identify <span className='font-bold'>opportunities</span> or potential leads
                                </li>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    Provide <span className='font-bold'>custom prompts</span> for scheduling and
                                    follow-ups
                                </li>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    Deliver <span className='font-bold'>quick insights</span> into your network's
                                    financial health and news
                                </li>
                            </ul>
                        </div>

                        <h2 className='font-bold text-2xl mb-3'>Personal Contacts & Files</h2>
                        <div className='text-[15px] leading-7 mb-10'>
                            <div>
                                <span className='font-bold'>LDR.ai</span> helps you:
                            </div>

                            <ul className='text-left inline-block'>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    <span className='font-bold'>Organize contacts</span> into private or shared lists
                                </li>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    Store, search and <span className='font-bold'>manage files</span> by associating
                                    them with recent news, meetings, projects, or organizations
                                </li>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    Quickly <span className='font-bold'>search</span> your entire network and documents
                                </li>
                            </ul>
                        </div>

                        <h2 className='font-bold text-2xl mb-3'>Your Privacy & Security</h2>
                        <div className='text-[15px] leading-7 mb-10'>
                            <div>
                                We only <span className='font-bold'>use your data</span> for:
                            </div>

                            <ul className='text-left inline-block'>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    <span className='font-bold'>Calendar syncing & AI suggestions in LDR.ai</span>
                                </li>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    <span className='font-bold'>No</span> third-party sharing or selling of personal
                                    information
                                </li>
                                <li className='relative pl-4'>
                                    <span className='inline-block w-[5px] h-[5px] bg-white rounded-full absolute left-0 top-[11px]'></span>
                                    Secure storage, <span className='font-bold'>GDPR compliance, & user-control</span>{' '}
                                    of permissions
                                </li>
                            </ul>
                        </div>

                        <h2 className='font-bold text-2xl mb-3'>Start streamlining your leadership now</h2>
                        <p className='text-[15px] leading-7'>
                            Sign up or learn more about how{' '}
                            <span className='font-bold'>LDR.ai can transform your workflow</span> — all while keeping
                            your data safe and under your control.
                        </p>
                    </ScrollPanel>
                    <div className='wave-loader'>
                        <div className='center-circle text-theme1-primary' onClick={handleEnter}>
                            Enter
                        </div>
                        <div className='wave'></div>
                        <div className='wave'></div>
                        <div className='wave'></div>
                    </div>
                    <div className='flex items-center justify-center gap-3 mt-20'>
                        <Link to='/privacy-policy' className='text-white text-xs'>
                            Privacy Policy
                        </Link>
                        <span className='inline-block w-[5px] h-[5px] bg-white rounded-full'></span>
                        <Link to='/terms-and-conditions' className='text-white text-xs'>
                            Terms and conditions
                        </Link>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Homepage;
